import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setStatusType('error');
      setStatusMessage('Please enter the email address associated with your account.');
      return;
    }

    setIsSubmitting(true);
    setStatusType('idle');
    setStatusMessage('');

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Unable to send reset instructions. Please try again.');
      }

      setStatusType('success');
      setStatusMessage('If an account exists for this email, we sent password reset instructions.');
    } catch (error: any) {
      setStatusType('error');
      setStatusMessage(error.message || 'An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="text-center pt-8 pb-6">
        <div className="inline-block">
          <button
            onClick={() => navigate('/')}
            className="block hover:opacity-90 transition-opacity focus:outline-none"
          >
            <img 
              src="/images/logo.jpg"
              alt="Rukuse Technology"
              className="h-32 w-auto"
            />
          </button>
        </div>
        <h1 className="text-5xl font-extrabold text-black tracking-wide mt-4">
          SMS MANAGEMENT PLATFORM
        </h1>
      </div>

      {/* Banner Section */}
      <div className="w-full mb-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="relative h-[300px] overflow-hidden">
            <img 
              src="/images/banner.jpg"
              alt="Business Communication" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Forgot Password Content */}
      <div className="container mx-auto px-4 flex-grow">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-black mb-4 text-center">Forgot Password</h2>
            <p className="text-gray-600 text-center mb-6">
              Enter the email tied to your Bulk SMS Platform account and we&apos;ll send you a link to reset your password.
            </p>

            {statusType === 'success' && (
              <div className="mb-4 rounded border border-green-200 bg-green-50 px-4 py-3 text-green-700">
                {statusMessage}
              </div>
            )}

            {statusType === 'error' && (
              <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                {statusMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="forgot-password-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="forgot-password-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="you@company.com"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white p-3 rounded hover:bg-gray-800 transition duration-200 disabled:opacity-70"
              >
                {isSubmitting ? 'Sending instructions...' : 'Send Reset Link'}
              </button>
            </form>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-6 w-full text-sm text-black hover:underline focus:outline-none"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-sm">
        © Rukuse Technology (Private) Limited
      </div>
    </div>
  );
};

